import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Cat,
  Dog,
  FileText,
  FlaskConical,
  LineChart,
  Paperclip,
  Pencil,
  Sparkles,
  Zap,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  getAnaliseIaControllerObterDoExameQueryKey,
  useAnaliseIaControllerGerarDoExame,
  useAnaliseIaControllerObterDoExame,
} from '@/api/generated/análise-por-ia/análise-por-ia';
import {
  useExamesControllerLaudoEspecial,
  useExamesControllerObter,
} from '@/api/generated/exames/exames';
import type {
  AnexoDoExameResponse,
  PacienteResponseEspecie,
  PacienteResponseSexo,
  TipoDeLaudoEspecialResponse,
} from '@/api/generated/model';
import { usePacientesControllerObter } from '@/api/generated/pacientes/pacientes';
import { ApiError } from '@/api/http-client';
import { PainelDeAnaliseIa } from '@/features/analises/components/PainelDeAnaliseIa';
import { AnexoDoExameDialogo } from '@/features/exames/components/AnexoDoExameDialogo';
import { EditarValoresExternosDialogo } from '@/features/exames/components/EditarValoresExternosDialogo';
import { descreverHorasAposMedicacao } from '@/features/exames/horasAposMedicacao';
import { LaudoEspecialDialogo } from '@/features/exames/components/LaudoEspecialDialogo';
import {
  CLASSE_STATUS,
  formatarData,
  formatarInstante,
  ICONE_INTERPRETACAO,
  rotuloDeOrigem,
  ROTULO_INTERPRETACAO,
  ROTULO_STATUS,
} from '@/features/exames/rotulos';
import { ESPECIES, formatarIdade, SEXOS } from '@/features/pacientes/lib/especies';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { ROTAS, rotaDoHistorico } from '@/shared/lib/rotas';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

/**
 * Laudo do exame — a tela que a V1 chamava de "Resultados" (`Resultados.tsx`).
 *
 * Mantém a arquitetura visual da V1: barra de navegação no topo, coluna
 * esquerda com os cards de contexto (paciente, exame) e coluna direita, com o
 * dobro da largura, para o resultado. Somente leitura.
 *
 * ⚠️ **O botão "Gerar laudo" da V1 não existe mais.** Ele chamava
 * `window.print()` — imprimia a TELA, com menu e cards, e não emitia laudo
 * nenhum. No lugar entrou "Visualizar laudo", que abre o PDF oficial, e só
 * aparece para os exames que TÊM laudo especial (Vitamina D, PTH+Cálcio,
 * B12+Ácido Fólico). Esse PDF é renderizado pelo CRM, a partir da O.S. do
 * laboratório: é o mesmo motor que a tela de Geração de Laudo de lá usa, para
 * não existirem dois layouts para manter. Exame comum fica sem botão — o
 * `Ctrl+P` do navegador continua funcionando, e os `print:hidden` abaixo
 * seguem valendo para ele.
 *
 * ⚠️ **Uma diferença é do modelo de dados, não de gosto.** Na V1 um exame ERA
 * um analito — a tabela tinha `resultado_valor` e `unidade` como colunas — e
 * por isso a tela mostrava um valor só, com os demais analitos da mesma O.S.
 * virando ABAS. Na V2 um exame tem N analitos numa tabela própria, então o
 * bloco de resultado é uma tabela: uma linha por analito, com faixa e
 * interpretação. Reproduzir as abas aqui seria fragmentar o que hoje é um
 * laudo só.
 *
 * ⚠️ **O card "Dados do Laboratório" da V1 não existe.** Ele mostrava
 * `nome_exame_vertis`, clínica de origem, solicitante com CRMV, método e
 * material — campos que o `ExameResponse` da V2 não tem. Quando o sync (B9)
 * trouxer esses dados, o card entra aqui.
 */
export function LaudoDoExamePage() {
  const { id = '' } = useParams();
  const clienteDeQuery = useQueryClient();
  const [laudoAberto, setLaudoAberto] = useState<TipoDeLaudoEspecialResponse | null>(null);
  const [anexoAberto, setAnexoAberto] = useState<AnexoDoExameResponse | null>(null);
  const [editandoValores, setEditandoValores] = useState(false);

  const { data: exame, isPending, isError, refetch } = useExamesControllerObter(id);

  // O PDF do laudo especial é renderizado pelo CRM; a API só diz quais existem
  // para esta O.S. Falha aqui não é problema — a resposta é `tipos: []` e o
  // botão some. Por isso `retry: false`: se o CRM está fora, insistir só atrasa.
  const { data: laudoEspecial } = useExamesControllerLaudoEspecial(id, {
    query: { enabled: Boolean(id), retry: false },
  });
  const tiposDeLaudo = laudoEspecial?.tipos ?? [];

  const { data: paciente } = usePacientesControllerObter(exame?.pacienteId ?? '', {
    query: { enabled: Boolean(exame?.pacienteId) },
  });

  // Lê a análise salva. Quem GERA é o `POST` abaixo — antes só no clique do
  // botão, agora também sozinho, pelo efeito logo adiante.
  const {
    data: analiseIa,
    isPending: iaCarregando,
    error: iaErro,
  } = useAnaliseIaControllerObterDoExame(id, { query: { enabled: Boolean(id) } });

  const gerarIa = useAnaliseIaControllerGerarDoExame({
    mutation: {
      // A resposta do POST já É a análise nova: escrever no cache evita uma
      // segunda ida ao servidor para buscar o que acabou de chegar.
      onSuccess: (gerada) => {
        clienteDeQuery.setQueryData(getAnaliseIaControllerObterDoExameQueryKey(id), gerada);
      },
      onError: (erro) => {
        toast.error(
          erro instanceof ApiError ? erro.message : 'Não conseguimos gerar a análise agora.',
        );
      },
    },
  });

  const { pode } = usePermissoes();
  const podeGerarIa = pode('analises_ia.criar');

  // Não tenta de novo sozinho depois de uma falha (ficaria em loop de retry a
  // cada render) — só reseta quando o exame muda. Recarregar a página tenta
  // de novo, e é o caminho de recuperação: não sobrou botão para isso.
  const tentouAutoGerar = useRef(false);
  useEffect(() => {
    tentouAutoGerar.current = false;
  }, [id]);

  useEffect(() => {
    if (!id || iaCarregando || gerarIa.isPending || tentouAutoGerar.current) return;
    // 503 é ambiente sem IA configurada — não é falta de análise para gerar.
    if (iaErro instanceof ApiError && iaErro.status === 503) return;
    if (!podeGerarIa) return;

    const precisaGerar = !analiseIa?.existe || analiseIa.desatualizada || analiseIa.baseDesatualizada;
    if (!precisaGerar) return;

    tentouAutoGerar.current = true;
    gerarIa.mutate({ id });
  }, [id, iaCarregando, iaErro, podeGerarIa, analiseIa, gerarIa]);

  if (isPending) return <EstadoCarregando rotulo="Carregando laudo..." />;

  if (isError || !exame) {
    return (
      <div className="space-y-6">
        <EstadoErro
          titulo="Exame não encontrado"
          descricao="Ele pode ter sido excluído, ou o endereço está errado."
          aoTentarDeNovo={() => void refetch()}
        />
        <div className="flex justify-center">
          <Button asChild variant="outline">
            <Link to={ROTAS.exames}>Voltar aos exames</Link>
          </Button>
        </div>
      </div>
    );
  }

  const IconeEspecie = paciente?.especie === 'FELINO' ? Cat : Dog;

  // `null` aqui significa NÃO ESCREVA NADA — nem a linha, nem um "—". Ver
  // `descreverHorasAposMedicacao`.
  const horasRelatadas = descreverHorasAposMedicacao(exame.horasAposMedicacao);

  // No externo os valores vieram do laudo de OUTRO laboratório, e quem os lançou
  // foi uma pessoa conferindo a leitura do documento — por isso podem ser
  // corrigidos aqui. Nos demais, o caminho é o lançamento de resultados.
  const externo = exame.origem === 'EXTERNO';
  const podeEditarValores = externo && pode('resultados.editar');

  // ⚠️ `?? []` contra um campo que o tipo diz existir sempre: o frontend pode
  // entrar no ar antes do backend que passou a devolvê-lo, e nesse intervalo
  // `.length` de `undefined` derrubaria a tela de TODO laudo — não só a do
  // externo. A lista vazia é a leitura certa de "o servidor ainda não manda".
  const semAnalito = (exame.resultadosSemAnalito as typeof exame.resultadosSemAnalito | undefined) ?? [];

  return (
    <div className="space-y-6">
      {/* `print:hidden`: a V1 imprimia a tela inteira, com menu e botões. */}
      <div className="flex items-center justify-between flex-wrap gap-2 print:hidden">
        <Button asChild variant="ghost">
          <Link to={ROTAS.exames}>
            <ArrowLeft className="w-4 h-4" />
            Voltar aos exames
          </Link>
        </Button>

        <Button asChild variant="outline" className="border-brand/40">
          <Link to={rotaDoHistorico(exame.pacienteId)}>
            <LineChart className="w-4 h-4" />
            Ver histórico e evolução
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <Card className="bg-surface-raised border-2 border-brand/20">
            <CardHeader className="border-b border-brand/10">
              <CardTitle className="flex items-center gap-3 text-base">
                {paciente?.fotoUrl ? (
                  <img
                    src={paciente.fotoUrl}
                    alt={exame.pacienteNome}
                    className="w-10 h-10 rounded-xl object-cover border border-brand/20"
                  />
                ) : (
                  <span className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center">
                    <IconeEspecie className="w-5 h-5 text-brand" />
                  </span>
                )}
                {exame.pacienteNome}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <Linha
                rotulo="Espécie"
                valor={ESPECIES[exame.pacienteEspecie as PacienteResponseEspecie]}
              />
              {paciente && (
                <>
                  <Linha rotulo="Raça" valor={paciente.raca} />
                  <Linha rotulo="Sexo" valor={SEXOS[paciente.sexo as PacienteResponseSexo]} />
                  <Linha rotulo="Idade" valor={formatarIdade(paciente.idadeMeses)} />
                  <Linha
                    rotulo="Peso"
                    valor={paciente.pesoKg === null ? null : `${paciente.pesoKg} kg`}
                  />
                  <div className="pt-4 border-t border-border">
                    <Linha rotulo="Responsável" valor={paciente.tutorNome} />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card className="bg-surface-raised border-2 border-brand/20">
            <CardHeader className="border-b border-brand/10">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold text-brand uppercase tracking-wide">
                <FlaskConical className="w-4 h-4" />
                Informações do exame
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <Linha rotulo="O.S." valor={exame.os} mono />
              <Linha rotulo="Exame" valor={exame.exameNome} destaque />
              <Linha rotulo="Origem" valor={rotuloDeOrigem(exame.origem)} />
              {exame.laboratorio && <Linha rotulo="Laboratório" valor={exame.laboratorio} />}
              <Linha rotulo="Coleta" valor={formatarData(exame.coletadoEm)} />
              {/* ⚠️ Só aparece quando existe, e sempre com o "informado pelo
                  tutor" junto. Uma linha "não informado" a mais em cada exame
                  empurraria para baixo o que se veio ler; e o número sem a
                  ressalva se lê como medição, que ele não é. */}
              {horasRelatadas && <Linha rotulo="Após a medicação" valor={horasRelatadas} />}
              {exame.metodoAnalitico && (
                <Linha rotulo="Método analítico" valor={exame.metodoAnalitico} />
              )}
              <Linha rotulo="Liberação" valor={formatarData(exame.liberadoEm)} />
              <Linha rotulo="Solicitado em" valor={formatarInstante(exame.criadoEm)} />
              <Linha rotulo="Veterinário" valor={exame.veterinarioNome} />
              {exame.urgente && (
                <Badge variant="outline" className="text-destructive border-destructive/40">
                  <Zap className="w-3 h-3 mr-1" />
                  Urgente
                </Badge>
              )}
              {exame.observacoes && (
                <div className="pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    Observações
                  </p>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">
                    {exame.observacoes}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* O card de resultado deixou de ser filho direto do grid para dar
            lugar ao painel de IA logo abaixo dele — a análise interpreta a
            tabela que fica em cima. */}
        <div className="lg:col-span-2 space-y-6 self-start">
          <Card className="bg-surface-raised border-2 border-brand/20">
            <CardHeader className="border-b border-brand/10 flex flex-row items-center justify-between gap-3">
              <CardTitle className="text-sm font-semibold text-brand uppercase tracking-wide">
                Resultado do exame
              </CardTitle>
              <Badge variant="outline" className={CLASSE_STATUS[exame.status]}>
                {ROTULO_STATUS[exame.status] ?? exame.status}
              </Badge>
            </CardHeader>

            <CardContent className="pt-6 space-y-6">
              {/* Exame externo: o resultado É o documento. Fica acima da tabela
                  porque, quando ele existe, é o conteúdo do laudo — e não um
                  apêndice dele. */}
              {exame.anexos.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">
                    {exame.anexos.length === 1 ? 'Documento' : 'Documentos'}
                  </p>
                  <ul className="space-y-1.5">
                    {exame.anexos.map((anexo) => (
                      <li key={anexo.id}>
                        <button
                          type="button"
                          onClick={() => setAnexoAberto(anexo)}
                          className="flex w-full items-center gap-2 rounded-md border border-brand/20 px-3 py-2 text-left hover:border-brand/50"
                        >
                          <Paperclip className="w-4 h-4 shrink-0 text-brand" aria-hidden="true" />
                          <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                            {anexo.nomeOriginal}
                          </span>
                          <span className="shrink-0 text-xs text-muted-foreground">Abrir</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* O texto do laudo externo — no exame de imagem, é ELE o resultado. */}
              {exame.laudoTexto && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">
                    {exame.imagem ? 'Laudo de imagem' : 'Laudo'}
                  </p>
                  <p className="text-sm text-foreground whitespace-pre-line">{exame.laudoTexto}</p>
                </div>
              )}

              {exame.resultados.length === 0 ? (
                // `totalAnalitos === 0` é o exame externo sem valor ligado a
                // analito: não há analito a esperar, então "aguardando
                // resultado" seria mentira — e "nenhum dos 0 analitos" seria
                // pior ainda.
                exame.totalAnalitos === 0 ? (
                  exame.anexos.length === 0 &&
                  !exame.laudoTexto &&
                  semAnalito.length === 0 && (
                    <EstadoVazio
                      icone={FileText}
                      titulo="Sem documento"
                      descricao="Este exame externo foi registrado sem nenhum arquivo anexado."
                    />
                  )
                ) : (
                  <EstadoVazio
                    icone={FlaskConical}
                    titulo="Aguardando resultado"
                    descricao={`Nenhum dos ${exame.totalAnalitos} analitos foi lançado até agora.`}
                  />
                )
              ) : (
                <>
                  {externo ? (
                    // ⚠️ As duas ressalvas vão escritas: de onde o número veio, e
                    // com que régua ele foi lido. O laudo de fora traz a faixa
                    // DELE, e a coluna "Interpretação" abaixo usa a do catálogo —
                    // sem dizer isso, "Acima" ao lado de um valor que o papel
                    // chama de normal parece erro do sistema.
                    <p className="text-xs text-muted-foreground">
                      Valores transcritos do laudo
                      {exame.laboratorio ? ` de ${exame.laboratorio}` : ' de outro laboratório'}. A
                      interpretação usa a faixa de referência do catálogo, não a impressa no laudo.
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {exame.resultados.length} de {exame.totalAnalitos} exames lançados.
                    </p>
                  )}

                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Exames</TableHead>
                          <TableHead className="text-right">Valor</TableHead>
                          <TableHead>Unidade</TableHead>
                          <TableHead>Referência</TableHead>
                          <TableHead>Interpretação</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {exame.resultados.map((resultado) => {
                          const chave = resultado.interpretacao ?? 'INDETERMINADO';
                          const { icone: Icone, classe } =
                            ICONE_INTERPRETACAO[chave] ?? ICONE_INTERPRETACAO.INDETERMINADO!;

                          return (
                            <TableRow key={resultado.id}>
                              <TableCell className="font-medium text-foreground">
                                {resultado.analitoNome}
                              </TableCell>
                              <TableCell className="text-right font-medium text-foreground">
                                {resultado.valorNumerico?.toLocaleString('pt-BR') ??
                                  resultado.valorTexto ?? (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                              </TableCell>
                              <TableCell className="text-muted-foreground">
                                {resultado.unidade ?? '—'}
                              </TableCell>
                              <TableCell className="text-muted-foreground whitespace-nowrap">
                                {resultado.faixaDeReferencia ?? (
                                  <span className="italic">Não cadastrada para esta espécie</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <span className="flex items-center gap-2">
                                  <span
                                    className={`w-6 h-6 rounded flex items-center justify-center ${classe}`}
                                  >
                                    <Icone className="w-3.5 h-3.5" />
                                  </span>
                                  <span className="text-sm text-foreground">
                                    {ROTULO_INTERPRETACAO[chave] ?? chave}
                                  </span>
                                </span>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {exame.resultados.some((r) => r.observacao) && (
                    <div className="pt-4 border-t border-border space-y-2">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">
                        {externo ? 'Observações por valor' : 'Observações do laboratório'}
                      </p>
                      {exame.resultados
                        .filter((r) => r.observacao)
                        .map((r) => (
                          <p key={r.id} className="text-sm text-muted-foreground">
                            <span className="text-foreground font-medium">{r.analitoNome}:</span>{' '}
                            {r.observacao}
                          </p>
                        ))}
                    </div>
                  )}
                </>
              )}

              {semAnalito.length > 0 && (
                <div className="pt-4 border-t border-border space-y-2">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">
                      Outros valores do laudo
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Sem analito correspondente no catálogo — ficam registrados aqui e não entram
                      nos gráficos.
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome no laudo</TableHead>
                          <TableHead className="text-right">Valor</TableHead>
                          <TableHead>Unidade</TableHead>
                          <TableHead>Referência no laudo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {semAnalito.map((linha, indice) => (
                          // Sem id: a lista vem de uma coluna JSON, e o mesmo
                          // nome pode se repetir (basal e pós-ACTH).
                          <TableRow key={`${linha.nome}-${indice}`}>
                            <TableCell className="font-medium text-foreground">{linha.nome}</TableCell>
                            <TableCell className="text-right font-medium text-foreground">
                              {linha.valor}
                            </TableCell>
                            <TableCell className="text-muted-foreground">{linha.unidade ?? '—'}</TableCell>
                            <TableCell className="text-muted-foreground">{linha.referencia ?? '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {podeEditarValores && (
                <div className="flex justify-end pt-4 border-t border-border print:hidden">
                  <Button variant="outline" onClick={() => setEditandoValores(true)}>
                    <Pencil className="w-4 h-4" />
                    {exame.resultados.length + semAnalito.length > 0
                      ? 'Editar valores'
                      : 'Lançar valores'}
                  </Button>
                </div>
              )}

              {/* Laudo especial: só aparece quando o CRM tem um para esta O.S.
                  Exame comum não tem botão nenhum — o que havia aqui antes era
                  um `window.print()` chamado de "Gerar laudo", que imprimia a
                  TELA (menu, cards e tudo) e não emitia o laudo oficial. */}
              {tiposDeLaudo.length > 0 && (
                <div className="flex justify-end flex-wrap gap-2 pt-4 border-t border-border print:hidden">
                  {tiposDeLaudo.map((tipo) => (
                    <Button key={tipo.codigo} onClick={() => setLaudoAberto(tipo)}>
                      <FileText className="w-4 h-4" />
                      {/* Com um tipo só, o rótulo do exame seria redundante com
                          o card. Com dois ou mais, ele é o que os diferencia. */}
                      {tiposDeLaudo.length === 1 ? 'Visualizar laudo' : tipo.rotulo}
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ⚠️ `print:hidden`, e é decisão de produto, não de estilo.
            Uma hipótese de IA impressa dentro de um laudo se confunde com
            achado do laboratório — quem recebe o papel não sabe que aquela
            parte foi escrita por um modelo. Se um dia for para imprimir, tem
            de ser em seção própria e rotulada, nunca como continuação da
            tabela de analitos acima. */}
          <Card className="bg-surface-raised border-2 border-brand/20 print:hidden">
            <CardHeader className="border-b border-brand/10">
              <CardTitle className="text-sm font-semibold text-brand uppercase tracking-wide flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Análise por IA
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <PainelDeAnaliseIa
                analise={analiseIa}
                carregando={iaCarregando}
                gerando={gerarIa.isPending}
                erro={iaErro}
                aoGerar={() => gerarIa.mutate({ id })}
                ocultarBotaoDeGerar
              />
            </CardContent>
          </Card>
        </div>
      </div>

      <LaudoEspecialDialogo
        exameId={id}
        tipo={laudoAberto}
        aoFechar={() => setLaudoAberto(null)}
      />

      <AnexoDoExameDialogo exameId={id} anexo={anexoAberto} aoFechar={() => setAnexoAberto(null)} />

      {podeEditarValores && (
        <EditarValoresExternosDialogo
          aberto={editandoValores}
          aoFechar={() => setEditandoValores(false)}
          exame={exame}
        />
      )}
    </div>
  );
}

/** Rótulo pequeno em caixa alta sobre o valor — o padrão de campo da V1. */
function Linha({
  rotulo,
  valor,
  mono,
  destaque,
}: {
  rotulo: string;
  valor?: ReactNode;
  mono?: boolean;
  destaque?: boolean;
}) {
  return (
    <div>
      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{rotulo}</p>
      <p
        className={`font-medium ${destaque ? 'text-brand' : 'text-foreground'} ${
          mono ? 'font-mono' : ''
        }`}
      >
        {valor ?? '—'}
      </p>
    </div>
  );
}
